/**
 * api/ui.ts —— 界面反馈能力
 *
 * 与 `explain` 的区别（这个区别很重要，容易混）：
 *   explain('文字')  →  说给用户**听**的话。是一个完整的回答。
 *   ui.toast('文字') →  一个**轻提示**。是动作的补充说明，会自己消失。
 *
 * 什么时候用 toast 而不是 explain：
 *   当一句话已经由别的指令的成功文案说过了，但还是想给个浮层强调时。
 *   例：`setting.setTheme('dark'); ui.toast('深色模式对眼睛更友好')`
 *
 * 频繁弹 toast 会烦人，所以提示词里会告诉模型"能不用就不用"。
 */

import { eventBus } from '@/core/event-bus';
import type { ObjectDef } from '../registry';

export const uiApi = {
  name: 'ui',
  desc: '界面上的轻提示与弹窗（不改数据，只影响观感）',
  userDesc: '显示轻提示、弹窗或加载状态',
  methods: [
    {
      name: 'toast',
      desc:
        '在界面底部弹一条自动消失的轻提示。只在需要额外强调时用，不要滥用 —— ' +
        '大部分情况下 explain 就够了，toast 是给"已经执行了动作、还想补一句"的场景。',
      params: [{ name: 'text', type: 'string' as const, desc: '提示文字', required: true }],
      successText: (args: unknown[]) => `已提示：${String(args[0])}`,
      run: (args: unknown[]) => {
        const text = String(args[0]);
        eventBus.emit('ui:toast', { text });
        return {};
      },
    },
    {
      name: 'modal',
      desc: '弹出一个需要用户点「知道了」的对话框。用于信息量大、不该一闪而过的内容。',
      params: [
        { name: 'title', type: 'string' as const, desc: '标题', required: true },
        { name: 'text', type: 'string' as const, desc: '正文', required: true },
      ],
      successText: (args: unknown[]) => `已弹出「${String(args[0])}」`,
      run: (args: unknown[]) => {
        const [title, text] = args as [string, string];
        // 通过事件走，让 ui 组件决定怎么渲染 —— API 层不直接碰 DOM
        eventBus.emit('ui:modal-open', { level: 'info', title, text });
        return {};
      },
    },
    {
      name: 'loading',
      desc:
        '显示一个常驻的加载提示条（不会自动消失）。只用于真的需要用户等待的场景，' +
        '比如耗时很长的批量操作。用完必须调用 ui.done() 收尾，否则提示会一直挂在屏幕上。',
      params: [{ name: 'text', type: 'string' as const, desc: '加载提示', required: true }],
      successText: () => '已显示加载提示',
      run: (args: unknown[]) => {
        // 刻意**不**动 uiStore.busy。
        //
        // busy 的语义是「AI 请求正在进行中」，它直接驱动输入栏那个转圈按钮。
        // 早先这里写的是 uiStore.set({ busy: true })，但没有任何地方把它复位 ——
        // 一旦 AI 发出 ui.loading(...)，输入栏就会永远转圈、永远无法再发送。
        // 加载提示是「一条会自己消失的提示」，和「请求在飞」是两回事，
        // 复用同一个状态字段只会让两者互相污染。
        eventBus.emit('ui:toast', { text: String(args[0]), sticky: true });
        return {};
      },
    },
    {
      name: 'done',
      desc: '收起之前用 ui.loading() 显示的加载提示。写了 ui.loading 就应该在忙完后调用它。',
      params: [],
      successText: () => '已收起加载提示',
      run: () => {
        // 空文本 + sticky 是 toast 组件约定的「清除常驻提示」信号
        eventBus.emit('ui:toast', { text: '', sticky: true });
        return {};
      },
    },
  ],
} satisfies ObjectDef;
